import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import Select from "../../components/Select";
import Api from "../../service/http";

const EditContact = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    category_ids: [],
    note: ""
  });

  const fetchCategories = async () => {
    try {
      const response = await Api.get('web/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchContact = async () => {
    try {
      const response = await Api.get(`web/contacts/${id}`);
      const contact = response.data;

      setForm({
        first_name: contact.first_name,
        last_name: contact.last_name,
        phone: contact.phone,
        email: contact.email || "",
        category_ids: contact.categories.map(cat => cat.id),
        note: contact.note || ""
      });
    } catch (error) {
      console.error('Error fetching contact:', error);
      alert('Failed to load contact details. Please try again.');
      navigate('/contacts');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchContact();
    } else {
      setInitialLoading(false);
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    const value = parseInt(e.target.value);
    setForm(prev => ({ ...prev, category_ids: [value] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await Api.put(`web/contacts/${id}`, form);
      alert(response.data.message || "Contact updated successfully!");
      navigate("/contacts");
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("Failed to update contact. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-20">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Edit Contact</h1>
          <p className="text-gray-500 text-sm mt-1">Update the contact information below.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              required
              placeholder="e.g. John"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              required
              placeholder="e.g. Doe"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              placeholder="e.g. +1 234 567 890"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="e.g. john@example.com"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <Select
              label="Category"
              value={form.category_ids[0] || ""}
              onChange={handleCategoryChange}
              options={categories?.map(cat => ({ label: cat.name, value: cat.id }))}
              placeholder="Select Category"
            />
          </div>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">
            Note
          </label>
          <textarea
            name="note"
            value={form.note}
            onChange={handleChange}
            placeholder="Add any additional information here..."
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm h-32 resize-none"
          />
        </div>

            <div className="flex justify-end items-center gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={() => navigate("/contacts")}
                className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all text-sm shadow-md shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : "Update Contact"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditContact;